/**
 * Whitelist of paths the /auth `redirect` query param is allowed to land on
 * after a successful login (password or OAuth). Anything not in this list
 * — including off-site URLs, protocol-relative paths (`//evil.com`), and
 * unknown app routes — is sanitized to the fallback (`/`).
 *
 * Keep this in sync with the protected/user-area routes declared in
 * `src/App.tsx`. Public marketing routes are intentionally excluded:
 * there is no reason to bounce through /auth to reach them.
 */
export const ALLOWED_REDIRECT_PATHS = [
  "/profile",
  "/orders",
  "/myaccount",
  "/favorites",
  "/checkout",
  "/notifications",
  "/rewards",
  "/referral",
  "/healthReport",
  "/tracker",
  "/admin",
  "/vendor",
] as const;

export type AllowedRedirectPath = (typeof ALLOWED_REDIRECT_PATHS)[number];

/**
 * Returns a safe `pathname+search` string for the post-login redirect, or
 * the fallback (default `/`) if the input is missing, malformed, off-site,
 * or not on the allow-list. Query strings are preserved; fragments and
 * any embedded auth are stripped.
 *
 * Fragments (`#...`) are intentionally dropped. They never affect routing
 * decisions and have historically been abused to smuggle payloads past
 * naive allow-list checks (e.g. `/profile#//evil.com`). Callers that need
 * an in-page anchor after login should re-apply it on the destination.
 */
export function safeRedirect(
  raw: string | null | undefined,
  fallback = "/",
): string {
  if (!raw || typeof raw !== "string") return fallback;
  // Must be a site-relative path, not protocol-relative or absolute.
  if (!raw.startsWith("/") || raw.startsWith("//")) return fallback;
  let parsed: URL;
  try {
    parsed = new URL(raw, "http://_lovable_redirect_base_");
  } catch {
    return fallback;
  }
  // Reject anything that resolved off the dummy origin (e.g. backslash tricks).
  if (parsed.origin !== "http://_lovable_redirect_base_") return fallback;
  if (!(ALLOWED_REDIRECT_PATHS as readonly string[]).includes(parsed.pathname)) {
    return fallback;
  }
  return parsed.pathname + parsed.search;
}